// src/components/ui/UploadQueueIndicator.tsx

import React, { useState, useEffect, useCallback } from "react";
import UploadQueueManager, {
  UploadQueueItem,
} from "../../utils/uploadQueueManager";
import { ReactComponent as Audio } from "../../assets/icons/svg/audio.svg";
import { ReactComponent as Screenshot } from "../../assets/icons/svg/screenshot.svg";
import { ReactComponent as Video } from "../../assets/icons/svg/video.svg";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface UploadQueueIndicatorProps {
  cardId?: number;
  onComplete?: (completedItems: UploadQueueItem[]) => void;
  variant?: "sidebar" | "popup";
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  showCompleted?: boolean;
}

const UploadQueueIndicator: React.FC<UploadQueueIndicatorProps> = ({
  cardId,
  onComplete,
  variant = "sidebar",
  position = "bottom-right",
  showCompleted = true,
}) => {
  const [uploadItems, setUploadItems] = useState<UploadQueueItem[]>([]);
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());

  const positionClasses = {
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
  };

  const updateUploadItems = useCallback(() => {
    if (cardId) {
      const pending = UploadQueueManager.getPendingForCard(cardId);
      const failed = UploadQueueManager.getFailedForCard(cardId);
      const completed = UploadQueueManager.getCompletedForCard(cardId);

      // Auto-remove failed items from the queue so they don't appear in the list
      failed.forEach((item) => UploadQueueManager.removeItem(item.id));

      const allItems = [...pending];

      if (showCompleted) {
        const recentlyCompleted = completed.filter(
          (item) => !completedItems.has(item.id),
        );

        // ✅ CRITICAL FIX: Ensure ALL completed items show 100% progress
        const completedWith100Progress = recentlyCompleted.map((item) => ({
          ...item,
          progress: 100, // Force 100%
          status: "completed" as const,
        }));

        // Add completed items to display
        allItems.push(...completedWith100Progress);

        // Mark as seen and schedule removal
        recentlyCompleted.forEach((item) => {
          setCompletedItems((prev) => new Set(prev).add(item.id));

          console.log("✅ Showing completed item at 100%:", {
            id: item.id,
            type: item.type,
            progress: 100,
          });

          // ✅ Wait 2.5 seconds showing completed at 100%, then trigger refresh and remove
          setTimeout(() => {
            console.log(
              "🔄 Triggering refresh after 100% display for:",
              item.id,
            );

            // Trigger refresh in ViewListFeedback
            if (onComplete) {
              onComplete([item]);
            }

            // Remove from display and queue after refresh triggered
            setTimeout(() => {
              console.log("🧹 Removing completed item from display:", item.id);

              setCompletedItems((prev) => {
                const newSet = new Set(prev);
                newSet.delete(item.id);
                return newSet;
              });

              // Clean up from queue
              UploadQueueManager.removeItem(item.id);
            }, 500); // Wait 500ms before removing
          }, 2500); // Show completed status for 2.5 seconds
        });
      }

      setUploadItems(allItems);

      if (
        pending.length === 0 &&
        failed.length === 0 &&
        completed.length > 0 &&
        onComplete
      ) {
        onComplete(completed);
      }
    }
  }, [cardId, completedItems, onComplete, showCompleted]);

  useEffect(() => {
    updateUploadItems();

    const handleQueueUpdate = () => {
      updateUploadItems();
    };

    window.addEventListener("upload-queue-updated", handleQueueUpdate);
    const interval = setInterval(updateUploadItems, 1000);

    return () => {
      window.removeEventListener("upload-queue-updated", handleQueueUpdate);
      clearInterval(interval);
    };
  }, [updateUploadItems]);

  if (uploadItems.length === 0) {
    return null;
  }

  // ✅ SIDEBAR VARIANT - Matches ViewListFeedback feedback item style
  if (variant === "sidebar") {
    return (
      <>
        {uploadItems.map((item) => (
          <div
            key={item.id}
            className="p-3 w-full h-28 3xl:h-32 border-b-[0.5px] border-[#D5D9EA] bg-white transition-all duration-300 relative"
            style={{
              opacity:
                item.status === "completed" && item.progress === 100 ? 1 : 1,
            }}
          >
            <div className="flex items-start justify-center gap-3">
              {/* Profile Icon */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-gray-100">
                <div className="w-4 h-4 animate-spin">
                  <svg
                    className="w-full h-full text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pr-8">
                <p
                  className="text-[12px] 2xl:text-[14px] 3xl:text-[16px] text-[#1E1F26]  font-medium mb-2"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {item.status === "failed"
                    ? `Upload failed - ${item.type === "screenshot" ? "Screenshot" : "Recording"}`
                    : item.status === "completed"
                      ? `✓ Upload complete - ${item.type === "screenshot" ? "Screenshot" : "Recording"}`
                      : item.progress === 100 && item.status === "uploading"
                        ? `Finalizing ${item.type === "screenshot" ? "screenshot" : "recording"}...`
                        : `Uploading ${item.type === "screenshot" ? "screenshot" : "recording"}...`}
                </p>

                {/* Error Message */}
                {item.status === "failed" && item.error && (
                  <p
                    className="text-[10px] 2xl:text-[12px] text-red-600 mt-1 line-clamp-1"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {item.error}
                  </p>
                )}
              </div>

              {/* Order Number Placeholder */}
              <div className="absolute bottom-3 left-3 flex items-center gap-7">
                <span
                  className="text-xs 3xl:text-[14px] text-[#1E1F26] font-medium "
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  --
                </span>
              </div>

              {/* Type Icons */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1">
                {item.hasAudio && <Audio className="w-5 h-5" />}
                {item.type === "screenshot" ? (
                  <Screenshot className="w-5 h-5" />
                ) : (
                  <Video className="w-5 h-5" />
                )}
              </div>
            </div>

            {/* Progress Bar - Absolutely Positioned in Center */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-3rem)] px-12">
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    item.status === "completed"
                      ? "bg-green-500"
                      : "bg-[#1E1F26]"
                  }`}
                  style={{
                    width: `${item.progress}%`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }

  // ✅ POPUP VARIANT - Original floating popup style
  return (
    <div
      className={`fixed ${positionClasses[position]} z-9999 max-w-sm w-full px-4`}
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3
              className="text-sm font-medium text-gray-900"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Uploading{" "}
              {uploadItems.length > 1 ? `(${uploadItems.length})` : ""}
            </h3>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto custom-scrollbar-overlay">
          {uploadItems.map((item) => (
            <div
              key={item.id}
              className={`p-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                item.status === "completed"
                  ? "bg-green-50"
                  : item.status === "failed"
                    ? "bg-red-50"
                    : "bg-white"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-0.5">
                  {item.type === "screenshot" ? (
                    <Screenshot className="w-8 h-8" />
                  ) : (
                    <Video className="w-8 h-8" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {item.type === "screenshot"
                        ? "Screenshot"
                        : "Screen Recording"}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        item.status === "completed"
                          ? "text-green-600"
                          : item.status === "failed"
                            ? "text-red-600"
                            : "text-blue-600"
                      }`}
                    >
                      {item.status === "completed" && "✓ Done"}
                      {item.status === "failed" && "✗ Failed"}
                      {item.status === "uploading" && `${item.progress}%`}
                      {item.status === "pending" && "Pending..."}
                    </span>
                  </div>

                  {(item.status === "uploading" ||
                    item.status === "pending") && (
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          item.status === "pending"
                            ? "bg-gray-400"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}

                  {item.status === "failed" && item.error && (
                    <p
                      className="text-xs text-red-600 mt-1 line-clamp-2"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      {item.error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UploadQueueIndicator;
